import { CapacityCalculator } from './capacity-calculator.js';
import { CapacityTasks } from './capacity-tasks.js';

/**
 * Capacity UI Module
 * Handles the main UI rendering and interaction for the capacity component
 */
export class CapacityUI {
  constructor(container, dataManager, taskHub = null) {
    this.container = container;
    this.data = dataManager;
    this.taskHub = taskHub;

    // Initialize sub-modules
    this.calculator = new CapacityCalculator(dataManager);
    this.tasks = new CapacityTasks(dataManager);

    // State
    this.currentView = 'daily'; // daily/weekly
    this.currentMode = 'circle'; // circle/cup
    this.isModalOpen = false;
    this.themeObserver = null;

    this.init();
  }

  init() {
    this.tasks.loadTasks();
    this.render();
    this.attachEventListeners();
    this.startCapacityMonitoring();
    this.observeThemeChanges();
  }

  render() {
    const capacityUsed = this.getCapacityUsed();
    const completedCapacity = this.getCompletedCapacity();
    const normalizedUsed = Math.min(100, Math.max(0, capacityUsed));
    const normalizedCompleted = Math.min(100, Math.max(0, completedCapacity));
    const displayUsed = Math.round(normalizedUsed);
    const displayCompleted = Math.round(normalizedCompleted);
    const displayLeft = Math.max(0, 100 - displayUsed);
    const isOverCapacity = normalizedUsed > 100;
    const modalClass = this.isModalOpen ? 'is-open' : '';
    const statusMessage = this.getStatusMessage(displayUsed, isOverCapacity);

    this.container.innerHTML = `
      <div class="card card--nature">
        <div class="card__header">
          <h3 class="card__title">
            <span class="emoji">⚡</span>
            Your Energy Today
          </h3>
          <div class="energy-actions">
            <button class="btn btn--small" id="enough-open-planner">Open planner</button>
          </div>
        </div>

        <div class="card__content">
          <div class="capacity-visualization">
            <div class="capacity-circle">
              <svg class="capacity-svg" viewBox="0 0 100 100">
                <!-- Background circle -->
                <circle cx="50" cy="50" r="45" class="capacity-bg" />

                <!-- Completed capacity (green) -->
                <circle cx="50" cy="50" r="45"
                        class="capacity-completed"
                        stroke-dasharray="${normalizedCompleted * 2.83} ${283 - normalizedCompleted * 2.83}"
                        transform="rotate(-90 50 50)" />

                <!-- Used capacity (blue) -->
                <circle cx="50" cy="50" r="45"
                        class="capacity-used"
                        stroke-dasharray="${normalizedUsed * 2.83} ${283 - normalizedUsed * 2.83}"
                        transform="rotate(-90 50 50)" />

                <!-- Center content -->
                <circle cx="50" cy="50" r="35" class="capacity-center" />
                <text x="50" y="45" class="capacity-percentage" text-anchor="middle">${displayUsed}%</text>
                <text x="50" y="60" class="capacity-label" text-anchor="middle">Used</text>
              </svg>

              ${isOverCapacity ? '<div class="capacity-overflow">⚠️</div>' : ''}
            </div>

            <div class="capacity-info">
              <div class="capacity-stats">
                <div class="stat-item">
                  <span class="stat-label">Used</span>
                  <span class="stat-value">${displayUsed}%</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Left</span>
                  <span class="stat-value">${displayLeft}%</span>
                </div>
                <div class="stat-item">
                  <span class="stat-label">Completed</span>
                  <span class="stat-value">${displayCompleted}%</span>
                </div>
              </div>

              <div class="capacity-message ${statusMessage.type}">
                <p>${statusMessage.text}</p>
              </div>

              <div class="capacity-actions">
                <button class="btn btn--primary btn--small" id="add-task-btn">
                  + Add Task
                </button>
                <button class="btn btn--secondary btn--small" id="view-tasks-btn">
                  View Tasks
                </button>
              </div>
            </div>
          </div>

          <!-- Task List (shown when View Tasks is clicked) -->
          <div class="task-list-container" id="task-list-container" style="display: none;">
            <div class="task-list-header">
              <h4>Your Tasks Today</h4>
              <button class="btn btn--tiny" id="hide-tasks-btn">Hide</button>
            </div>
            <div class="task-list" id="task-list">
              ${this.renderTaskList()}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  renderTaskList() {
    const activeTasks = this.tasks.getActiveTasks();

    if (activeTasks.length === 0) {
      return '<p class="task-empty">No tasks added yet. Click "Add Task" to get started!</p>';
    }

    return activeTasks.map(task => `
      <div class="task-item ${task.completed ? 'completed' : ''}" data-task-id="${task.id}">
        <div class="task-checkbox">
          <input type="checkbox" ${task.completed ? 'checked' : ''} data-action="toggle-complete">
        </div>
        <div class="task-content">
          <div class="task-name">${this.escapeHtml(task.name)}</div>
          <div class="task-meta">
            <span class="task-duration">${task.duration}min</span>
            <span class="task-category">${task.category}</span>
          </div>
        </div>
        <div class="task-actions">
          <button class="btn btn--tiny" data-action="delete">×</button>
        </div>
      </div>
    `).join('');
  }

  getCapacityUsed() {
    return this.tasks.getActiveTasks()
      .filter(task => !task.completed)
      .reduce((total, task) => total + (task.duration || 0), 0);
  }

  getCompletedCapacity() {
    return this.tasks.getActiveTasks()
      .filter(task => task.completed)
      .reduce((total, task) => total + (task.duration || 0), 0);
  }

  getStatusMessage(percentage, isOverCapacity) {
    if (isOverCapacity) {
      const overBy = percentage - 100;
      return {
        text: `Over capacity by ${Math.round(overBy)}%`,
        type: 'over',
        color: '#ef4444',
      };
    }

    if (percentage >= 90) {
      return {
        text: 'Almost full - consider prioritizing',
        type: 'high',
        color: '#f59e0b',
      };
    }

    if (percentage >= 75) {
      return {
        text: 'Getting full - room for 1-2 more tasks',
        type: 'medium',
        color: '#10b981',
      };
    }

    if (percentage >= 50) {
      return {
        text: 'Half full - good momentum!',
        type: 'good',
        color: '#3b82f6',
      };
    }

    if (percentage >= 25) {
      return {
        text: 'Light day - plenty of capacity left',
        type: 'light',
        color: '#8b5cf6',
      };
    }

    return {
      text: 'Fresh start - what will you accomplish?',
      type: 'fresh',
      color: '#06b6d4',
    };
  }

  escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  attachEventListeners() {
    // Add task button
    const addTaskBtn = this.container.querySelector('#add-task-btn');
    if (addTaskBtn) {
      addTaskBtn.addEventListener('click', () => {
        this.tasks.showTaskAdditionChoice();
      });
    }

    // View tasks button
    const viewTasksBtn = this.container.querySelector('#view-tasks-btn');
    if (viewTasksBtn) {
      viewTasksBtn.addEventListener('click', () => {
        this.toggleTaskList();
      });
    }

    // Hide tasks button
    const hideTasksBtn = this.container.querySelector('#hide-tasks-btn');
    if (hideTasksBtn) {
      hideTasksBtn.addEventListener('click', () => {
        this.toggleTaskList();
      });
    }

    // Task list interactions
    const taskList = this.container.querySelector('#task-list');
    if (taskList) {
      taskList.addEventListener('click', (e) => {
        const action = e.target.dataset.action;
        const taskItem = e.target.closest('.task-item');
        if (!taskItem) return;

        const taskId = taskItem.dataset.taskId;

        switch (action) {
          case 'toggle-complete':
            this.tasks.toggleTaskCompletion(taskId);
            this.render();
            break;
          case 'delete':
            if (confirm('Delete this task?')) {
              this.tasks.deleteTask(taskId);
              this.render();
            }
            break;
        }
      });
    }

    // Open planner button
    const openPlannerBtn = this.container.querySelector('#enough-open-planner');
    if (openPlannerBtn) {
      openPlannerBtn.addEventListener('click', () => {
        // This would typically open the capacity planner modal
        // For now, just show a notification
        if (typeof this.onOpenPlanner === 'function') {
          this.onOpenPlanner();
        } else {
          alert('Capacity planner would open here');
        }
      });
    }
  }

  toggleTaskList() {
    const taskListContainer = this.container.querySelector('#task-list-container');
    if (taskListContainer) {
      const isVisible = taskListContainer.style.display !== 'none';
      taskListContainer.style.display = isVisible ? 'none' : 'block';
    }
  }

  startCapacityMonitoring() {
    // Update capacity visualization periodically
    setInterval(() => {
      this.updateTimeCardRingColors();
    }, 60000); // Update every minute

    // Initial update
    this.updateTimeCardRingColors();
  }

  updateTimeCardRingColors() {
    const timeCards = this.container.querySelectorAll('.time-card');
    const now = new Date();
    const currentHour = now.getHours();

    timeCards.forEach((card, index) => {
      const cardHour = (currentHour + index) % 24;
      const ring = card.querySelector('.time-card__ring');

      if (ring) {
        let color;
        if (cardHour >= 6 && cardHour < 18) {
          // Daytime - use warmer colors
          const intensity = Math.min(1, (cardHour - 6) / 12);
          color = `rgba(255, ${200 + intensity * 55}, ${100 + intensity * 100}, 0.8)`;
        } else {
          // Nighttime - use cooler colors
          const intensity = cardHour >= 18 ? Math.min(1, (cardHour - 18) / 6) : Math.min(1, (6 - cardHour) / 6);
          color = `rgba(${100 + intensity * 100}, ${100 + intensity * 155}, ${200 + intensity * 55}, 0.8)`;
        }

        ring.style.background = `conic-gradient(from 0deg, ${color} 0%, ${color} 25%, transparent 25%, transparent 100%)`;
      }
    });
  }

  observeThemeChanges() {
    if (typeof MutationObserver !== 'undefined') {
      this.themeObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
            this.render(); // Re-render to update colors
          }
        });
      });

      this.themeObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['data-theme'],
      });
    }
  }

  // Public API methods
  refresh() {
    this.tasks.loadTasks();
    this.render();
  }

  setOnTaskAdded(callback) {
    this.calculator.onTaskAdded = callback;
  }

  setOnOpenPlanner(callback) {
    this.onOpenPlanner = callback;
  }
}
